import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  DOCUMENT,
  inject,
  input,
  NgZone,
  OnInit,
  output,
  Signal,
  signal,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  betterLatestFrom,
  cancelParticipationEventById,
  ConfigService,
  createEvent,
  currentUser,
  currentUserSettings,
  DataService,
  deleteEventById,
  eventPatched,
  extractMaxAvailabilityWithin,
  filter as filterSelector,
  finishEventById,
  getEventFinishRequests,
  formatDurationMinutesForDisplay,
  generateAvailability,
  getAvailabilityFromView,
  getIndexFromDate,
  getOnlineMeetingLink,
  getSubAvailabilityFromAvailability,
  getTextKeyAndClass,
  getTotallAvailability,
  getResourceTimeZone,
  injectAreas,
  isAllDayEvent,
  loadEventsPatched,
  loadFavourites,
  mergeAvailabilities,
  refreshDashboard,
  ReservationWithHint,
  RepetitiveEventsService,
  ResourceAttendee,
  ResourceService,
  selectUser,
} from '@assist/shared/data';
import { translation } from '@cue/translations';
import { AttendeesDropdownComponent, AttendeesDropdownItemComponent } from '@assist/ui/attendees-dropdown';
import { QuillLoadDirective } from '@assist/ui/structural';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import {
  DropdownlistComponent,
  DropdownsItemTemplateDirective,
  ValueTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { LoadingComponent } from '@cue/loading';
import { LocalizedStringPipe, QRService, RtlDirective } from '@cue/utilities';
import {
  cueClose,
  cueDelete,
  cueDirections,
  cueExclamationCircle,
  cueOrders,
  cueShuffle,
  cueStar,
  cueStarFill,
  cueCaretLeftFill,
  cueCaretRightFill,
  IconComponent,
  cueFinish,
  cueCheckCircle,
} from '@cue/ui/icons';
import { FormBuilder, FormControl, FormControlStatus, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { QuillEditorComponent } from 'ngx-quill';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { add, addDays, addMinutes, differenceInMinutes, endOfDay, isSameDay, min, set, startOfDay } from 'date-fns';
import {
  ApiService,
  AssistResourceBM,
  CalendarSchedulingSettings,
  eventModified,
  fromLocalized,
  getResourceIsAllowedMultipleResourceType,
  OrderDescriptor,
  OrderItemDescriptor,
  profileSettings,
  WebexUserSettingsModel,
} from '@cue/api';
import {
  CalendarAvailability,
  CalendarEventDatetime,
  CalendarEventEditScope,
  CalendarRecurringMutationScope,
  CalendarEventRecurrence,
  CalendarsService,
  expandRecurrenceOccurrences,
  resolveValidationWindowEnd,
} from '@cue/calendars';
import {
  CalendarA11y,
  CalendarCommonModule,
  CalendarDateFormatter,
  CalendarDayModule,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarEventTitleFormatter,
  CalendarUtils,
  DateAdapter,
} from 'angular-calendar';
import { AvailabilityColorPipe, ResourceTitlePipe } from '@assist/ui/pipes';
import { DatetimeInfoComponent, DateTimeSeparateInfoComponent } from '@cue/assist/ui/datetime-info';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CustomDateFormatter } from './custom-date-formatter';
import { ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent } from '@cue/modal';
import { LicensingService } from '@cue/licensing';
import { localized } from '@cue/translate';

import {
  FinishReservationData,
  ReservationDeleteComponent,
  ReservationFinishComponent,
} from '@assist/ui/reservation-popups';

declare global {
  interface String {
    replaceAll(find: string, value: string): string;
  }
}

interface AttendeeDropdownItem {
  text: string;
  value: string;
  initials: string;
  color: string;
  isGroup: boolean;
}

interface Visibility {
  value: 'public' | 'private';
  text: string;
}

enum NavigationLinkHintType {
  'NONE',
  'ASSIST',
  'NAVIGATION',
  'BOTH',
}

enum RecurringOverLimitActionId {
  Disallow = 1,
  ScopeOut = 2,
  IgnoreAndWarn = 3,
}

type RecurringSchedulingValidationResult = {
  effectiveRecurrence: CalendarEventRecurrence;
  blockingReasons: string[];
  warningReasons: string[];
};

import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { derivedAsync } from 'ngxtension/derived-async';
import { toSignal } from '@angular/core/rxjs-interop';
import { Attendee } from './attendee';
import { ReservationDetailModalData } from './reservation-detail-modal.data';
import type { NewReservationCreatedEventResult } from '../new-reservation-dialog/new-reservation-dialog.data';
import { randomColors } from './random.colors';
import { showAsMap } from './show-as.map';
import { visibilities } from './visibilities';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { CheckboxComponent, TextboxComponent } from '@cue/assist/ui/inputs';
import { TooltipDirective } from '@cue/ui/tooltips';
import { ClearableDirective } from '@cue/ui';
import { ReservationDetailOrdersTabComponent, ReservationDetailResourceTabComponent } from '@assist/info-tabs';
import { ExpansionPanelComponent } from '@progress/kendo-angular-layout';
import { DateFormatPipe, DateTimeFormatPipe, PipesService, SubjectPipe, TimeFormatPipe } from '@cue/pipes';
import { SwitchComponent } from '@progress/kendo-angular-inputs';
import { getNextQuarter, Pack } from '@cue/data';
import { formatInTimeZone } from 'date-fns-tz';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { RecurrenceEditorComponent } from '../recurrence-editor/recurrence-editor.component';
import {
  resolveRecurringMutationScope,
  resolveRecurringMutationTargetEventId,
  resolveSingleOccurrenceMutation,
} from '../../helpers/recurring-event-mutation.helpers';
import {
  replaceReservationPlaceholders,
  ReservationPlaceholderValues,
} from '../../helpers/reservation-placeholders.helpers';
import { formatSchedulingRulesForDisplay } from '../../helpers/reservation-scheduling-rules.helpers';
import {
  createAvailabilityDisableReason,
  hasErrorAvailabilityDisableReason,
  ReservationAvailabilityDisableReason as DisableReason,
  ReservationAvailabilityDisableReasonLevel as DisableReasonLevel,
} from '../../helpers/reservation-availability-reasons.helpers';

type ReminderValue = {
  minutes: number | null;
  text: string;
};

@Component({
  selector: 'assist-reservation-detail',
  templateUrl: './reservation-detail.component.html',
  styleUrls: ['./reservation-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    ReactiveFormsModule,
    AvailabilityColorPipe,
    TranslocoPipe,
    QuillLoadDirective,
    ButtonComponent,
    AttendeesDropdownComponent,
    ReservationDetailOrdersTabComponent,
    ReservationDetailResourceTabComponent,
    DatetimeInfoComponent,
    DateTimeSeparateInfoComponent,
    LoadingComponent,
    DropdownlistComponent,
    ClearableDirective,
    DropdownsItemTemplateDirective,
    ValueTemplateDirective,
    ResourceTitlePipe,
    AttendeesDropdownItemComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LocalizedStringPipe,
    IconComponent,
    CheckboxComponent,
    DateFormatPipe,
    DateTimeFormatPipe,
    TooltipDirective,
    SubjectPipe,
    ExpansionPanelComponent,
    RtlDirective,
    TextboxComponent,
    NgClass,
    DatePipe,
    NgStyle,
    SwitchComponent,
    RecurrenceEditorComponent,
    CalendarCommonModule,
    CalendarDayModule,
    QuillEditorComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    // pro calendar
    {
      provide: DateAdapter,
      useFactory: adapterFactory,
    },
    CalendarEventTitleFormatter,
    CalendarDateFormatter,
    CalendarUtils,
    CalendarA11y,
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter,
    },
  ],
})
export class ReservationDetailComponent implements OnInit {
  ICONS = {
    cueExclamationCircle,
    cueCheckCircle,
    cueFinish,
    cueDirections,
    cueShuffle,
    cueClose,
    cueStar,
    cueStarFill,
    cueDelete,
    cueCaretLeftFill,
    cueCaretRightFill,
  };

  // Services

  terminator = injectDestroy();
  dateTimeFormatPipe = inject(DateTimeFormatPipe);
  licensingService = inject(LicensingService);
  pipesService = inject(PipesService);
  translocoService = inject(TranslocoService);
  document = inject(DOCUMENT);
  timeFormatPipe = inject(TimeFormatPipe);
  dateFormatPipe = inject(DateFormatPipe);
  dataService = inject(DataService);
  authService = inject(AuthService);
  datePipe = inject(DatePipe);
  apiService = inject(ApiService);
  store = inject(Store);
  configService = inject(ConfigService);
  zone = inject(NgZone);
  fb = inject(FormBuilder);
  resourceService = inject(ResourceService);
  router = inject(Router);
  calendarService = inject(CalendarsService);
  repetitiveEventsService = inject(RepetitiveEventsService);
  dialog = inject(Dialog);
  modal = inject(DialogRef);
  dialogData = inject<ReservationDetailModalData>(DIALOG_DATA);
  // End picker to detect changes
  endPicker = viewChild(DateTimeSeparateInfoComponent);
  readonly dateTimePickers = viewChildren(DateTimeSeparateInfoComponent);
  readonly dateTimeValid = computed(() => this.dateTimePickers().every((picker) => picker.valid()));
  translation = translation;
  isColleague = input(false);

  allDayControl = new FormControl(false);
  daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  queryParamTab = linkedQueryParam('detailTab', {
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });

  // PRIMITIVES
  reminderValues: ReminderValue[] = [
    {
      minutes: null,
      text: this.translocoService.translate(translation.reservationPopup.reminder.dontRemind),
    },
    {
      minutes: 0,
      text: this.translocoService.translate(translation.reservationPopup.reminder.justInTime),
    },
    {
      minutes: 5,
      text: this.translocoService.translate(translation.reservationPopup.reminder.fiveMinutes),
    },
    {
      minutes: 10,
      text: this.translocoService.translate(translation.reservationPopup.reminder.tenMinutes),
    },
    {
      minutes: 15,
      text: this.translocoService.translate(translation.reservationPopup.reminder.fifteenMinutes),
    },
    {
      minutes: 30,
      text: this.translocoService.translate(translation.reservationPopup.reminder.thirtyMinutes),
    },
    {
      minutes: 60,
      text: this.translocoService.translate(translation.reservationPopup.reminder.oneHour),
    },
    {
      minutes: 120,
      text: this.translocoService.translate(translation.reservationPopup.reminder.twoHours),
    },
    {
      minutes: 720,
      text: this.translocoService.translate(translation.reservationPopup.reminder.twelveHours),
    },
    {
      minutes: 1440,
      text: this.translocoService.translate(translation.reservationPopup.reminder.oneDay),
    },
    {
      minutes: 10080,
      text: this.translocoService.translate(translation.reservationPopup.reminder.oneWeek),
    },
  ];

  showAsMap = showAsMap;
  visibilities = visibilities;
  tabs = [
    this.translocoService.translate(translation.reservationPopup.reservation),
    this.translocoService.translate(translation.reservationPopup.detail),
    this.configService.value.enableOrders ? this.translocoService.translate(translation.orders.orders) : undefined,
  ].filter((x) => !!x) as string[];

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ header: 1 }, { header: 2 }, { header: 3 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
  };

  navigationHintValues = [
    {
      key: NavigationLinkHintType.NONE,
      name: this.translocoService.translate(this.translation.reservationPopup.none),
    },
    this.configService.value.navigationEnabled
      ? {
          key: NavigationLinkHintType.ASSIST,
          name: 'CUE Assist',
        }
      : undefined,
    this.configService.value.navigationAppEnabled
      ? {
          key: NavigationLinkHintType.NAVIGATION,
          name: 'CUE Navigation',
        }
      : undefined,
    this.configService.value.navigationAppEnabled && this.configService.value.navigationEnabled
      ? {
          key: NavigationLinkHintType.BOTH,
          name: this.translocoService.translate(this.translation.reservationPopup.both),
        }
      : undefined,
  ].filter((x) => !!x) as unknown as { key: NavigationLinkHintType; name: string }[];

  pageIndex = signal(0);
  uiState = signal<'view' | 'edit' | 'loading'>('view');
  mode = signal<'detail' | 'new'>(this.dialogData.mode);
  now = set(new Date(), { seconds: 0 });

  start = signal(this.now, {
    equal: (previous, current) => previous?.getTime() == current?.getTime(),
  });
  end = signal(this.now, {
    equal: (previous, current) => previous?.getTime() == current?.getTime(),
  });
  minimum = computed(() => startOfDay(this.start()));
  maximum = computed(() => startOfDay(addDays(this.end(), 1)));
  // Events
  close = output<void>();

  activeTab = linkedQueryParam('detailTab', {
    queryParamsHandling: 'merge',
    replaceUrl: true,
    parse: (v) => (this.dialogData.forcedTab !== undefined ? this.dialogData.forcedTab : v ? parseInt(v) : 0),
    stringify: (v) => v?.toString(),
  });
  editForm = this.fb.group({
    reminder: new FormControl(this.reminderValues[0]),
    visibility: this.fb.control(visibilities[0]),
    webex: this.fb.control(this.configService.value.autoMeetingSettings_Webex),
    teams: this.fb.control(this.configService.value.autoMeetingSettings_Teams),
    meet: this.fb.control(this.configService.value.autoMeetingSettings_Meet),
    showAs: this.fb.control({
      key: 'reservationPopup.free',
      value: 'free',
    }),
    subject: this.fb.control(''),
    bodyHtml: this.fb.control(''),
    navigationLinkHint: this.fb.control(this.navigationHintValues[0]),
    recurrence: this.fb.control<CalendarEventRecurrence | null>(null),
    editWholeSeries: this.fb.control(false),
  });

  recurrenceValue = toSignal(this.editForm.controls.recurrence.valueChanges, {
    initialValue: this.editForm.controls.recurrence.value,
  });

  editWholeSeriesValue = toSignal(this.editForm.controls.editWholeSeries.valueChanges, {
    initialValue: this.editForm.controls.editWholeSeries.value,
  });

  /**
   * Data for availability computation
   */
  dataForAvailability = computed(
    () => {
      return {
        end: this.end(),
        start: this.start(),
        minimum: this.minimum(),
        maximum: this.maximum(),
        resourceAttendees: this.dialogData.reservation.resourceAttendees,
        mode: this.mode(),
        recurrence: this.recurrenceValue(),
        editWholeSeries: this.editWholeSeriesValue(),
      };
    },
    {
      equal: (previous, current) =>
        previous.end.getTime() == current.end.getTime() &&
        previous.start.getTime() == current.start.getTime() &&
        previous.minimum.getTime() == current.minimum.getTime() &&
        previous.maximum.getTime() == current.maximum.getTime() &&
        previous.resourceAttendees == current.resourceAttendees &&
        JSON.stringify(previous.recurrence) === JSON.stringify(current.recurrence) &&
        previous.editWholeSeries === current.editWholeSeries,
    }
  );

  /**
   * Availability of the resource
   */
  availability = derivedAsync(
    () => {
      const availabilityRequest =
        this.dataForAvailability().resourceAttendees.length > 0 &&
        this.dataForAvailability().minimum < this.dataForAvailability().maximum
          ? forkJoin(
              ...this.dataForAvailability().resourceAttendees.map((rA) => {
                return this.dataService.getTimelinePatchedScheduleForResource(
                  rA.resource,
                  this.dataForAvailability().minimum,
                  this.dataForAvailability().maximum,
                  this.dataForAvailability().mode !== 'new'
                    ? {
                        start: this.dialogData.reservation.start.utcDateTime,
                        end: getNextQuarter(addMinutes(this.dialogData.reservation.end.utcDateTime, -1)),
                      }
                    : null
                );
              })
            )
          : of(null);
      const multipleResourceTypeRestrictionRequest =
        this.dataForAvailability().resourceAttendees.length > 0 &&
        this.dataForAvailability().start < this.dataForAvailability().end
          ? this.apiService
              .call(
                getResourceIsAllowedMultipleResourceType({
                  emails: this.dataForAvailability().resourceAttendees.map((rA) => rA.resource.email),
                  start: this.dataForAvailability().start,
                  end: this.dataForAvailability().end,
                  iCalUid: this.dialogData.reservation.iCalUId ?? null,
                  currentEventId: this.dialogData.reservation.id ?? null,
                })
              )
              .pipe(
                map((response) =>
                  (response.resources ?? []).some(
                    (resource) => resource.notAllowedMultipleResourceReservationsForResourceType
                  )
                ),
                catchError(() => of(null))
              )
          : of(false);

      return forkJoin({
        avs: availabilityRequest,
        multipleResourceTypeRestricted: multipleResourceTypeRestrictionRequest,
      }).pipe(
        map(({ avs, multipleResourceTypeRestricted }) => {
          const mergedAvailability = mergeAvailabilities(avs);

          const eventAvailability =
            avs === null || avs.length === 0
              ? CalendarAvailability.tentative
              : getTotallAvailability(
                  getSubAvailabilityFromAvailability(
                    mergedAvailability,
                    this.dataForAvailability().minimum,
                    this.dataForAvailability().start,
                    this.dataForAvailability().end
                  )
                );

          const durationInMinutes = differenceInMinutes(
            this.dataForAvailability().end,
            this.dataForAvailability().start
          );

          let disableReasons: DisableReason[] = [];
          if (this.dataForAvailability().end <= new Date()) {
            disableReasons.push(
              this.toDisableReason(this.translocoService.translate(translation.reservations.disablePast), 'error')
            );
          }

          this.dataForAvailability().resourceAttendees.forEach((rA) => {
            const minViolation =
              rA.resource?.schedulingSettings?.minimumMeetingDurationInMinutes != null &&
              durationInMinutes < rA.resource.schedulingSettings?.minimumMeetingDurationInMinutes;
            const maxViolation =
              rA.resource?.schedulingSettings?.maximumMeetingDurationInMinutes != null &&
              durationInMinutes > rA.resource.schedulingSettings?.maximumMeetingDurationInMinutes;

            if (minViolation) {
              disableReasons.push(
                this.toDisableReason(
                  this.translocoService.translate(translation.reservations.disableMinimumDuration, {
                    duration: formatDurationMinutesForDisplay(
                      rA.resource.schedulingSettings?.minimumMeetingDurationInMinutes ?? 0,
                      (key) => this.translocoService.translate(key)
                    ),
                  }),
                  'error'
                )
              );
            }

            if (maxViolation) {
              disableReasons.push(
                this.toDisableReason(
                  this.translocoService.translate(translation.reservations.disableMaximumDuration, {
                    duration: formatDurationMinutesForDisplay(
                      rA.resource.schedulingSettings?.maximumMeetingDurationInMinutes ?? 0,
                      (key) => this.translocoService.translate(key)
                    ),
                  }),
                  'error'
                )
              );
            }
          });

          if (multipleResourceTypeRestricted === null) {
            disableReasons.push(
              this.toDisableReason(
                this.translocoService.translate(translation.newReservationUi.location.availabilityCouldNotBeChecked),
                'error'
              )
            );
          } else if (multipleResourceTypeRestricted) {
            disableReasons.push(
              this.toDisableReason(
                this.translocoService.translate(
                  translation.newReservationUi.location.multipleResourceTypeReservationsNotAllowed
                ),
                'error'
              )
            );
          }

          if (eventAvailability != CalendarAvailability.free) {
            disableReasons.push(
              this.toDisableReason(
                this.translocoService.translate(translation.reservations.disableAvailability),
                'error'
              )
            );
          }

          return {
            loading: false,
            reservable:
              eventAvailability == CalendarAvailability.free &&
              this.dataForAvailability().end > new Date() &&
              this.dataForAvailability().start <= this.dataForAvailability().end &&
              disableReasons.length === 0,
            availabilityView:
              mergedAvailability ??
              generateAvailability(
                this.dataForAvailability().minimum,
                this.dataForAvailability().maximum,
                CalendarAvailability.tentative
              ),
            eventAvailability: eventAvailability,
            disableReasons: disableReasons,
            repetitiveValidationResult: null,
          };
        }),
        switchMap((baseAvailability) => {
          const recurrence = this.resolveRecurrenceForSave();
          const shouldCheckRepetitiveAvailability =
            recurrence != null &&
            this.repetitiveEventsEnabled() &&
            (this.mode() === 'new' || this.selectedEditScope() === 'seriesMaster');

          if (!shouldCheckRepetitiveAvailability) {
            return of(baseAvailability);
          }

          const recurringSchedulingValidation = this.getRecurringSchedulingValidation(recurrence!);
          const recurringDisableReasons = [
            ...recurringSchedulingValidation.blockingReasons.map((reason) => this.toDisableReason(reason, 'error')),
            ...recurringSchedulingValidation.warningReasons.map((reason) => this.toDisableReason(reason, 'warning')),
          ];

          if (recurringSchedulingValidation.blockingReasons.length > 0) {
            return of({
              ...baseAvailability,
              reservable: false,
              disableReasons: [...baseAvailability.disableReasons, ...recurringDisableReasons],
            });
          }

          return this.calendarService
            .checkRepetitiveAvailability({
              start: CalendarEventDatetime.fromBrowserDatetime(this.start()),
              end: CalendarEventDatetime.fromBrowserDatetime(this.end()),
              recurrence: recurringSchedulingValidation.effectiveRecurrence,
              resourceEmails: this.dialogData.reservation.resourceAttendees.map((ra) => ra.resource.email),
            })
            .pipe(
              map((repetitiveValidationResult) => {
                const repetitiveAllowedBySchedulingRules = this.isRecurringConflictAllowedBySchedulingRules(
                  repetitiveValidationResult.conflictedOccurrences,
                  repetitiveValidationResult.totalOccurrences
                );
                const hasRepetitiveConflicts = repetitiveValidationResult.conflictedOccurrences > 0;
                const repetitiveDisableReasons = hasRepetitiveConflicts
                  ? [
                      this.toDisableReason(
                        this.translocoService.translate(translation.reservations.recurringConflictsDetected, {
                          conflicted: repetitiveValidationResult.conflictedOccurrences,
                          total: repetitiveValidationResult.totalOccurrences,
                        }),
                        repetitiveAllowedBySchedulingRules ? 'warning' : 'error'
                      ),
                    ]
                  : [];
                const mergedDisableReasons = [
                  ...baseAvailability.disableReasons,
                  ...recurringDisableReasons,
                  ...repetitiveDisableReasons,
                ];

                return {
                  ...baseAvailability,
                  reservable: baseAvailability.reservable && repetitiveAllowedBySchedulingRules,
                  disableReasons: mergedDisableReasons,
                  repetitiveValidationResult,
                };
              })
            );
        })
      );
    },
    {
      initialValue: {
        loading: true,
        reservable: false,
        eventAvailability: CalendarAvailability.tentative,
        availabilityView: generateAvailability(
          this.dataForAvailability().minimum,
          this.dataForAvailability().maximum,
          CalendarAvailability.tentative
        ),
        disableReasons: [],
        repetitiveValidationResult: null,
      },
    }
  );

  hasErrorDisableReason = computed(() => hasErrorAvailabilityDisableReason(this.availability().disableReasons));

  /**
   * Autocalculate ranges - when the popup is called without hints - mainly from /reservation/reserve/[ID]
   */
  autocalculateRangeIfNeccesary() {
    if (!this.dialogData.reservation.start && !this.dialogData.reservation.end) {
      // je potreba spocitat tu novou mez.
      const nextQuarterNow = getNextQuarter(new Date());
      const nowIndex = getIndexFromDate(this.minimum(), nextQuarterNow);

      const defaultDurationFill =
        this.dialogData.reservation.resourceAttendees[0].resource.defaultDurationFill ??
        this.configService.value.timelineDefaults.defaultDuration.fill;
      const defaultDurationHours =
        this.dialogData.reservation.resourceAttendees[0].resource.defaultDurationHours ??
        this.configService.value.timelineDefaults.defaultDuration.hours;
      const defaultDurationMinutes =
        this.dialogData.reservation.resourceAttendees[0].resource.defaultDurationMinutes ??
        this.configService.value.timelineDefaults.defaultDuration.minutes;

      const defaultDurationTotal = defaultDurationHours * 60 + defaultDurationMinutes;

      const minResourceTime = this.dialogData.reservation.resourceAttendees[0].resource.schedulingSettings
        ?.minimumMeetingDurationInMinutes
        ? this.dialogData.reservation.resourceAttendees[0].resource.schedulingSettings?.minimumMeetingDurationInMinutes
        : defaultDurationTotal;
      const maxResourceTime = this.dialogData.reservation.resourceAttendees[0].resource.schedulingSettings
        ?.maximumMeetingDurationInMinutes
        ? this.dialogData.reservation.resourceAttendees[0].resource.schedulingSettings?.maximumMeetingDurationInMinutes
        : defaultDurationTotal;

      const clampedDurationTotal = Math.max(minResourceTime, Math.min(maxResourceTime, defaultDurationTotal));

      let { leftIndex, rightIndex, leftDate, rightDate } = extractMaxAvailabilityWithin(
        this.availability().availabilityView,
        this.minimum(),
        nextQuarterNow
      );

      // pokud se filluje
      if (defaultDurationFill) {
        if (this.availability().availabilityView[nowIndex] === CalendarAvailability.free) {
          // rozrosteme se
          const numberOfBlocksRequired = rightIndex - leftIndex + 1;
          const maxDuration =
            this.dialogData.reservation.resourceAttendees[0].resource.schedulingSettings
              ?.maximumMeetingDurationInMinutes;
          if (maxDuration != null) {
            const maxDurationInBlocks = maxDuration / 15;
            if (numberOfBlocksRequired > maxDurationInBlocks) {
              rightDate = addMinutes(rightDate, maxDuration);
            }
          }
          this.dialogData.reservation.start = CalendarEventDatetime.fromBrowserDatetime(leftDate);
          this.dialogData.reservation.end = CalendarEventDatetime.fromBrowserDatetime(rightDate);
          this.start.set(leftDate);
          this.end.set(rightDate);
        } else {
          // fallbackujeme na default duration
          const leftDate = nextQuarterNow;
          const rightDate = addMinutes(leftDate, clampedDurationTotal);
          this.dialogData.reservation.start = CalendarEventDatetime.fromBrowserDatetime(leftDate);
          this.dialogData.reservation.end = CalendarEventDatetime.fromBrowserDatetime(rightDate);
          this.start.set(leftDate);
          this.end.set(rightDate);
        }
      } else {
        const leftDate = nextQuarterNow;
        const patchedRightDate = min([addMinutes(leftDate, clampedDurationTotal), rightDate]);
        this.dialogData.reservation.start = CalendarEventDatetime.fromBrowserDatetime(leftDate);
        this.dialogData.reservation.end = CalendarEventDatetime.fromBrowserDatetime(patchedRightDate);
        this.start.set(leftDate);
        this.end.set(patchedRightDate);
      }
    }
  }

  optionalAttendeesDropdownData = signal<AttendeeDropdownItem[]>([]);
  requiredAttendeesDropdownData = signal<AttendeeDropdownItem[]>([]);

  /**
   * Is current user in required attendees?
   */
  currentUserInAttendees() {
    return this.requiredAttendeesDropdownData().some(
      (x) => x.value.toLowerCase() === this.currentUserPack().data?.email
    );
  }

  /**
   * Current orders
   */
  currentOrders = signal<
    {
      resourceIdForFiltering: number;
      note: string;
      items: any[];
    }[]
  >([]);

  ordersValidity = signal<FormControlStatus>('VALID');

  currentOrdersAllItemsCount = computed(() => {
    return this.currentOrders().reduce((acc, it) => acc + it.items.length, 0);
  });

  /**
   * Type of the save button - service or confirm
   */
  saveButtonType = computed(() => {
    return this.currentOrdersAllItemsCount() > 0 ? 'service' : 'confirm';
  });

  /**
   * Save button icon - service or confirm
   */
  saveButtonIcon = computed(() => {
    return this.currentOrdersAllItemsCount() > 0 ? cueOrders : cueShuffle;
  });

  saveButtonText = computed(() => {
    if (this.mode() === 'new') {
      // new reservation
      return this.currentOrdersAllItemsCount() === 0
        ? translation.orders.reserveOnly
        : translation.orders.reserveAndOrder;
    } else {
      // editr
      if (this.uiState() === 'view') {
        // readonly
        return this.currentOrdersAllItemsCount() == 0
          ? translation.orders.changeOnly
          : translation.orders.changeAndOrder;
      } else {
        // edit mode
        return this.currentOrdersAllItemsCount() == 0
          ? translation.orders.saveOnly
          : translation.orders.saveOnlyAndOrder;
      }
    }
  });

  modalTitle = computed(() =>
    this.mode() === 'new'
      ? this.translocoService.translate(translation.reservationPopup.newReservation)
      : this.translocoService.translate(translation.reservationPopup.title)
  );

  repetitiveEventsEnabled = computed(
    () =>
      this.repetitiveEventsService.isEnabled() &&
      this.currentUserPack()?.data?.features.assistCreatingReccuringEvents === true
  );

  repetitiveEventOptions = computed(() =>
    this.repetitiveEventsService.getOptions({
      mode: this.mode(),
      eventType: this.dialogData.reservation.type,
    })
  );

  recurrenceEndDateMax = computed<Date | undefined>(() => {
    const strictestRecurringLimit = this.getStrictestRecurringLimit();
    if (!strictestRecurringLimit || this.configService.isGspace()) {
      return undefined;
    }

    return endOfDay(addDays(this.start(), strictestRecurringLimit.limit));
  });

  supportsSeriesScopeSwitch = computed(() => {
    return this.dialogData.reservation.type === 'occurrence' || this.dialogData.reservation.type === 'exception';
  });

  isOccurrenceLikeEvent = computed(() => {
    return this.dialogData.reservation.type === 'occurrence' || this.dialogData.reservation.type === 'exception';
  });

  selectedEditScope = computed<CalendarEventEditScope>(() => {
    const type = this.dialogData.reservation.type;
    if (type === 'seriesMaster') {
      return 'seriesMaster';
    }
    if (type === 'occurrence' || type === 'exception') {
      return this.editWholeSeriesValue() ? 'seriesMaster' : 'singleOccurrence';
    }
    return 'singleOccurrence';
  });

  canEditRecurrencePattern = computed(() => {
    if (!this.repetitiveEventsEnabled()) {
      return false;
    }

    if (this.mode() === 'new') {
      return this.repetitiveEventOptions().allowRepetitiveCreate;
    }

    if (!this.repetitiveEventOptions().allowRepetitiveEdit) {
      return false;
    }

    const type = this.dialogData.reservation.type;
    return type === 'singleInstance' || type === 'seriesMaster';
  });

  recurrenceTypeLabel = computed(() => {
    switch (this.dialogData.reservation.type) {
      case 'seriesMaster':
        return this.translocoService.translate(translation.reservationPopup.recurrenceTypeSeriesMaster);
      case 'occurrence':
        return this.translocoService.translate(translation.reservationPopup.recurrenceTypeOccurrence);
      case 'exception':
        return this.translocoService.translate(translation.reservationPopup.recurrenceTypeException);
      default:
        return this.translocoService.translate(translation.reservationPopup.recurrenceTypeSingleInstance);
    }
  });

  recurrenceSummary = computed(() => {
    const recurrence = this.dialogData.reservation.recurrence;
    if (!recurrence) {
      return '';
    }

    const patternType = recurrence.pattern.type;
    const interval = recurrence.pattern.interval ?? 1;
    const repeatInfo =
      interval > 1
        ? this.translocoService.translate(translation.reservationPopup.recurrenceSummaryEveryPatternInterval, {
            interval,
            pattern: this.translocoService.translate(`reservationPopup.recurrencePattern.${patternType}`),
          })
        : this.translocoService.translate(translation.reservationPopup.recurrenceSummaryEveryPattern, {
            pattern: this.translocoService.translate(`reservationPopup.recurrencePattern.${patternType}`),
          });

    const range = recurrence.range;
    if (range.type === 'numbered' && range.numberOfOccurrences != null) {
      return this.translocoService.translate(translation.reservationPopup.recurrenceSummaryOccurrences, {
        repeatInfo,
        occurrences: range.numberOfOccurrences,
      });
    }
    if (range.type === 'endDate' && range.endDate) {
      return this.translocoService.translate(translation.reservationPopup.recurrenceSummaryUntil, {
        repeatInfo,
        endDate: range.endDate,
      });
    }
    return this.translocoService.translate(translation.reservationPopup.recurrenceSummaryNoEnd, {
      repeatInfo,
    });
  });

  currentUserPack = toSignal(this.store.select(currentUser));

  areas = injectAreas();
  isNavigationHintEnabled = computed(() => {
    return (
      this.dialogData.reservation.resourceAttendees.some((x) => x.resource.navigationEnabled) &&
      (this.configService.value.navigationEnabled || this.configService.value.navigationAppEnabled)
    );
  });

  isAnyResourceInNavigateableArea = computed(() => {
    return this.dialogData.reservation.resourceAttendees.some((rA) =>
      rA.resource.mapInfos.some((mapInfo) => {
        const area = this.areas().find((a) => a.id === mapInfo.areaId);
        return area && !area.notIncludedInNavigation;
      })
    );
  });

  webexPaired: Signal<Pack<WebexUserSettingsModel>> = derivedAsync(
    () => {
      return this.apiService.call(profileSettings()).pipe(
        map(
          (r) =>
            ({
              loading: false,
              data: r.data,
            }) as Pack<WebexUserSettingsModel>
        )
      );
    },
    {
      initialValue: {
        loading: true,
        data: null,
      },
    }
  );

  canEditAsNotOrganizer = computed(() => {
    if (!this.configService.isTouchoneCalendar() || this.mode() == 'new') {
      return false;
    }
    const reservation = this.dialogData.reservation;

    const canEdit =
      (reservation.ownerEventId != null &&
        reservation.visibility.toLowerCase() == 'private' &&
        reservation.eventPermissions?.canEditParentEventPrivate) ||
      (reservation.visibility.toLowerCase() == 'public' && reservation.eventPermissions?.canEditParentEventPublic) ||
      (reservation.ownerEventId == null &&
        reservation.visibility.toLowerCase() === 'public' &&
        reservation.eventPermissions?.canEditPublic) ||
      (reservation.visibility.toLowerCase() === 'private' && reservation.eventPermissions?.canEditPrivate);

    return !reservation.isOrganizer && canEdit;
  });

  isEditable = computed(() => {
    return (
      this.mode() == 'new' ||
      (this.dialogData.reservation != null
        ? !this.dialogData.readonlyOther && this.dialogData.reservation.showChangeButton
        : false)
    );
  });

  isStartEditable = computed(() => {
    return (
      this.mode() == 'new' ||
      (this.dialogData.reservation
        ? this.dialogData.reservation.accepted && this.dialogData.reservation?.start.utcDateTime >= new Date()
        : false)
    );
  });

  getTextKeyForConfirmation(resourceAttendee: ResourceAttendee) {
    if (resourceAttendee.resource.confirmationSettings.requiresConfirmation) {
      return translation.reservations.confirmationRequiredBetween;
    }
    if (
      resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel &&
      resourceAttendee.resource.confirmationSettings.requiresConfirmationBySensor
    ) {
      return translation.reservations.confirmationSensorOrPanelRequiredBetween;
    }
    return resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel
      ? translation.reservations.confirmationPanelRequiredBetween
      : translation.reservations.confirmationSensorRequiredBetween;
  }

  currentEvent = computed(() => {
    const event: CalendarEvent = {
      title: 'Draggable event',
      start: this.start(),
      end: this.end(),
      resizable: {
        beforeStart: !this.isAllDay() && this.uiState() === 'edit' && this.isEditable() && this.isStartEditable(),
        afterEnd: !this.isAllDay() && this.uiState() === 'edit' && this.isEditable(),
      },
      draggable: !this.isAllDay() && this.uiState() === 'edit' && this.isEditable() && this.isStartEditable(),
    };
    return event;
  });

  calendarComponentEvents = computed(() => [this.currentEvent()]);

  filter = toSignal(this.store.select(filterSelector));

  originalAttendees = computed(() => {
    const atts = this.filter().attendees.map((a, index) => ({
      name: a.text,
      email: a.value,
      required: !a.optional,
      status: '',
      initials:
        a.text != null
          ? a.text.trim().split(' ').length > 1
            ? a.text.trim().split(' ')[0][0] + a.text.trim().split(' ')[1][0]
            : '@'
          : '@',
      color: randomColors[index % (randomColors.length - 1)],
    }));

    return this.mode() === 'new'
      ? atts
      : this.dialogData.reservation
        ? (this.dialogData.reservation.attendees ?? [])
            .filter((x) => x.resource !== true)
            .map(
              (a, index) =>
                ({
                  name: a.emailAddress.name ?? a.emailAddress.address,
                  email: a.emailAddress.address,
                  status: a.status.response,
                  required: a.type == 'required',
                  initials:
                    a.emailAddress.name != null
                      ? a.emailAddress.name.trim().split(' ').length > 1
                        ? a.emailAddress.name.trim().split(' ')[0][0] + a.emailAddress.name.trim().split(' ')[1][0]
                        : '@'
                      : '@',
                  color: randomColors[index % (randomColors.length - 1)],
                }) as Attendee
            )
        : [];
  });

  originalOptionalAttendees = computed(() => {
    return this.originalAttendees().filter((x) => !x.required);
  });

  originalRequiredAttendees = computed(() => {
    return this.originalAttendees().filter((x) => x.required);
  });

  incrementPage() {
    this.pageIndex.update((i) => i + 1);
  }

  decrementPage() {
    this.pageIndex.update((i) => i - 1);
  }

  nextPageAvailable = computed(() => {
    return endOfDay(addDays(this.start(), this.pageIndex() + 1)) <= startOfDay(addDays(this.end(), 1));
  });

  previousPageAvailable = computed(() => {
    return this.pageIndex() > 0;
  });

  viewDate = computed(() => {
    return addDays(this.start(), this.pageIndex());
  });

  showNavigationHint = computed(() => {
    return (
      !this.dialogData.reservation.iCalUId &&
      this.uiState() === 'edit' &&
      this.isNavigationHintEnabled() &&
      this.isAnyResourceInNavigateableArea()
    );
  });

  scrollOnEvent = effect(() => {
    if (this.viewDate()) {
      this.zone.runOutsideAngular(() =>
        setTimeout(() => {
          document.getElementById('event')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 200)
      );
    }
  });

  confirmFrom(resourceAttendee: ResourceAttendee) {
    return this.dateTimeFormatPipe.transform(resourceAttendee.confirmationMetadata.lowerBorderTime);
  }

  confirmTo(resourceAttendee: ResourceAttendee) {
    return this.dateTimeFormatPipe.transform(resourceAttendee.confirmationMetadata.higherBorderTime);
  }

  showPage = computed(() => {
    return !isSameDay(startOfDay(this.start()), startOfDay(this.end()));
  });

  // Todo: marian vice resourcu
  showAllDay = computed(() => {
    const resourceTimeZone = getResourceTimeZone(
      this.dialogData.reservation.resourceAttendees[0].resource.workingHours
    );
    const startTimeDay = formatInTimeZone(this.start(), resourceTimeZone, 'iiii');
    const indexOfStartTimeDay = this.daysOfWeek.indexOf(startTimeDay.toLowerCase());
    const slotAllDay = this.dialogData.reservation.resourceAttendees[0].resource.workingHours.workingHoursSetting.find(
      (x) => x.day === indexOfStartTimeDay && x.startPeriod == '00:00:00' && x.endPeriod == '00:00:00'
    );
    return slotAllDay != null;
  });
  _previousStart?: Date;
  _previousEnd?: Date;

  isAllDay = signal(false);

  anyOnlineMeetingAvailable = computed(() => {
    return (
      // O365
      this.configService.isO365() || this.configService.isGspace() || this.configService.value.webexEnabled
    );
  });

  readonly onlineMeetingLink = computed(() => {
    this.uiState();
    return getOnlineMeetingLink(this.dialogData.reservation, this.document);
  });

  setAllDay(isAllDay: boolean) {
    if (isAllDay) {
      this.isAllDay.set(true);
      this._previousStart = this.start();
      this._previousEnd = this.end();

      this.start.set(startOfDay(this.start()));
      this.end.set(endOfDay(this.end()));
    } else {
      this.isAllDay.set(false);
      if (this._previousStart && this._previousEnd) {
        this.start.set(this._previousStart);
        this.end.set(this._previousEnd);
      }
    }
  }

  userSettings = toSignal(this.store.select(currentUserSettings));

  ngOnInit() {
    this.autocalculateRangeIfNeccesary();
    if (
      (this.dialogData.reservation.isAllDay ||
        isAllDayEvent(this.dialogData.reservation.start.utcDateTime, this.dialogData.reservation.end.utcDateTime)) &&
      this.mode() !== 'new'
    ) {
      this.isAllDay.set(true);
      this.allDayControl.setValue(true);
    }
    this.allDayControl.valueChanges.subscribe((value) => {
      this.setAllDay(value);
    });

    this.optionalAttendeesDropdownData.set(
      this.originalAttendees()
        .filter((x) => !x.required)
        .map((x) => ({
          color: x.color,
          initials: x.initials,
          text: x.name,
          value: x.email,
          isGroup: false,
        }))
    );

    this.requiredAttendeesDropdownData.set(
      this.originalAttendees()
        .filter((x) => x.required)
        .map((x) => ({
          color: x.color,
          initials: x.initials,
          text: x.name,
          value: x.email,
          isGroup: false,
        }))
    );
    if (this.dialogData.reservation.start && this.dialogData.reservation.end) {
      this.setStartEnd({
        start: new Date(this.dialogData.reservation.start.utcDateTime),
        end: new Date(this.dialogData.reservation.end.utcDateTime),
      });
    }

    let foundShowAs = showAsMap[this.configService.getMode()].find(
      (x) => x.value.toUpperCase() === 'FREE' || x.value.toUpperCase() === 'TRANSPARENT'
    );

    // Todo. ma
    if (this.dialogData.reservation.resourceAttendees[0].resource.showAs) {
      foundShowAs = showAsMap[this.configService.getMode()].find(
        (x) => x.value.toUpperCase() === this.dialogData.reservation.resourceAttendees[0].resource.showAs.toUpperCase()
      );
    }

    let selectedReminderValue: ReminderValue = this.reminderValues[0];
    if (!this.dialogData.reservation.reminder) {
      const defaultReservationRemindTime = this.configService.value.reservationRemindTime;

      const userRemindTime = this.userSettings().data?.reservationRemindTime;
      const userRemindTimeSet = this.userSettings().data?.reservationRemindTimeSet;
      const remindTime = userRemindTimeSet ? userRemindTime : defaultReservationRemindTime;
      selectedReminderValue = this.reminderValues.find((x) => x.minutes === remindTime);
    } else {
      if (!this.dialogData.reservation.reminder.enabled) {
        selectedReminderValue = this.reminderValues.find((x) => x.minutes === null);
      } else {
        const foundReminder = this.reminderValues.find(
          (x) => x.minutes === this.dialogData.reservation.reminder.minutesBeforeStart
        );
        if (foundReminder) {
          selectedReminderValue = foundReminder;
        } else {
          const newValue = {
            minutes: this.dialogData.reservation.reminder.minutesBeforeStart,
            text: this.translocoService.translate(translation.reservationPopup.reminder.customMinutesBefore, {
              minutes: this.dialogData.reservation.reminder.minutesBeforeStart,
            }),
          };
          this.reminderValues = [newValue, ...this.reminderValues];
          selectedReminderValue = newValue;
        }
      }
    }

    const existingRecurrence = this.dialogData.reservation.recurrence ?? null;

    this.editForm.patchValue({
      reminder: selectedReminderValue,
      visibility: this.dialogData.reservation.visibility.toLowerCase() == 'public' ? visibilities[0] : visibilities[1],
      webex: this.dialogData.reservation.iCalUId == null ? this.configService.value.autoMeetingSettings_Webex : false,
      teams:
        this.dialogData.reservation.onlineMeetingProvider == null
          ? this.configService.value.autoMeetingSettings_Teams
          : this.dialogData.reservation.onlineMeetingProvider != null &&
            (this.dialogData.reservation.onlineMeetingProvider.toLowerCase().includes('teams') ||
              (this.configService.isGspace() && this.dialogData.reservation.onlineMeetingProvider != null)),
      meet:
        this.dialogData.reservation.onlineMeetingProvider == null
          ? this.configService.value.autoMeetingSettings_Meet
          : this.dialogData.reservation.onlineMeetingProvider.toLowerCase().includes('teams') ||
            (this.configService.isGspace() && this.dialogData.reservation.onlineMeetingProvider != null),
      subject: this.dialogData.reservation.subject,
      bodyHtml: this.dialogData.reservation.bodyHtml,
      showAs: this.dialogData.reservation.showAs ?? foundShowAs,
      recurrence: existingRecurrence,
      editWholeSeries: false,
    });

    this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        document.getElementById('event')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200)
    );

    if (this.dialogData.triggerChange) {
      this.edit();
    }

    if (this.dialogData.mode === 'new') {
      if (!this.dialogData.readonlyOther) {
        this.edit();
      }
    }
  }

  setStartEnd(value: { start: Date; end: Date }) {
    if (value.start) {
      this.start.set(set(value.start, { seconds: 0 }));
    }
    if (value.end) {
      this.end.set(set(value.end, { seconds: 0 }));
    }
    this.pageIndex.update((pI) => (isSameDay(value.start, value.end) ? 0 : pI));
  }

  closeDialog() {
    if (this.dialogData.allowCompact) {
      this.compactDialog();
      return;
    }

    if (this.modal) {
      this.modal.close(0);
      return;
    }

    this.close.emit();
  }

  compactDialog() {
    this.modal.close({
      action: 'compact',
      start: this.start(),
      end: this.end(),
    });
  }

  changeTab(activeTab) {
    this.activeTab.set(activeTab);
    if (this.activeTab() === 0) {
      this.zone.runOutsideAngular(() =>
        setTimeout(() => {
          document.getElementById('event')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 200)
      );
    }
  }

  onNavigate(resourceAttendee: ResourceAttendee) {
    this.modal?.close();
    this.router.navigate(['/navigation'], {
      queryParams: {
        targetResourceId: resourceAttendee.resource.idForFiltering,
      },
    });
  }

  onAttendeeValueChange(attendess: AttendeeDropdownItem[], type: 'optional' | 'required') {
    if (type === 'required') {
      this.requiredAttendeesDropdownData.set(attendess);
    } else {
      this.optionalAttendeesDropdownData.set(attendess);
    }
  }

  edit() {
    this.uiState.set('edit');
  }

  private resolveRecurrenceForSave(): CalendarEventRecurrence | null {
    return this.recurrenceValue();
  }

  private resolveEffectiveRecurrenceForSave(
    recurrence: CalendarEventRecurrence | null
  ): CalendarEventRecurrence | null {
    if (!recurrence) {
      return null;
    }

    return this.getRecurringSchedulingValidation(recurrence).effectiveRecurrence;
  }

  private resolvePatchTargetEventId() {
    return resolveRecurringMutationTargetEventId(this.dialogData.reservation, this.selectedEditScope(), (event) =>
      this.calendarService.getSingleOccurrenceMutationTargetEventId(event)
    );
  }

  private resolveMutationScopeForSingleAwareOperation(
    selectedEditScope: CalendarEventEditScope
  ): CalendarRecurringMutationScope {
    return resolveRecurringMutationScope(this.dialogData.reservation, selectedEditScope);
  }

  getSchedulingRulesForDisplay(settings?: CalendarSchedulingSettings | null): string[] {
    return formatSchedulingRulesForDisplay(settings, (key, params) => this.translocoService.translate(key, params), {
      recurringMaxAdvanceAsOccurrences: this.configService.isGspace(),
    });
  }

  private getRecurringOverLimitActionId(): RecurringOverLimitActionId {
    const action = this.configService.value.recurringOverLimitActionId;
    if (action === RecurringOverLimitActionId.ScopeOut || action === RecurringOverLimitActionId.IgnoreAndWarn) {
      return action;
    }

    return RecurringOverLimitActionId.Disallow;
  }

  private getStrictestRecurringLimit(): { limit: number; resourceName: string } | null {
    let strictest: { limit: number; resourceName: string } | null = null;

    for (const resourceAttendee of this.dialogData.reservation.resourceAttendees) {
      const limit = resourceAttendee.resource.schedulingSettings?.recurringMaxAdvanceDays;
      if (limit == null) {
        continue;
      }

      const resourceName =
        fromLocalized(
          resourceAttendee.resource.displayNameForApp ?? resourceAttendee.resource.displayName,
          this.translocoService.getActiveLang(),
          this.translocoService.getDefaultLang()
        ) ?? resourceAttendee.resource.name;

      if (strictest == null || limit < strictest.limit) {
        strictest = { limit, resourceName };
      }
    }

    return strictest;
  }

  private buildScopedRecurrence(
    recurrence: CalendarEventRecurrence,
    limit: number,
    isGspace: boolean,
    limitEndDateString: string
  ): CalendarEventRecurrence {
    if (isGspace) {
      return {
        ...recurrence,
        range: {
          ...recurrence.range,
          type: 'numbered',
          numberOfOccurrences: limit,
          endDate: undefined,
        },
      };
    }

    return {
      ...recurrence,
      range: {
        ...recurrence.range,
        type: 'endDate',
        endDate: limitEndDateString,
        numberOfOccurrences: undefined,
      },
    };
  }

  private getRecurringSchedulingValidation(recurrence: CalendarEventRecurrence): RecurringSchedulingValidationResult {
    const blockingReasons: string[] = [];
    const warningReasons: string[] = [];
    let effectiveRecurrence = recurrence;

    const start = this.start();
    const end = this.end();
    const startDatetime = CalendarEventDatetime.fromBrowserDatetime(start);
    const endDatetime = CalendarEventDatetime.fromBrowserDatetime(end);
    const providerValidationEnd = resolveValidationWindowEnd(
      start,
      this.repetitiveEventsService.getPolicy().validationWindow
    );
    const occurrences = expandRecurrenceOccurrences(
      recurrence,
      startDatetime,
      endDatetime,
      start,
      providerValidationEnd
    );
    const strictestRecurringLimit = this.getStrictestRecurringLimit();
    const isGspace = this.configService.isGspace();
    const recurringLimit = strictestRecurringLimit?.limit ?? null;
    const recurringLimitResourceName = strictestRecurringLimit?.resourceName ?? '';

    for (let i = 1; i < occurrences.length; i++) {
      const previousOccurrence = occurrences[i - 1];
      const currentOccurrence = occurrences[i];
      if (currentOccurrence.start.getTime() < previousOccurrence.end.getTime()) {
        blockingReasons.push(
          this.translocoService.translate(translation.reservations.recurringEventOccurrencesOverlap)
        );
        break;
      }
    }

    if (recurringLimit != null) {
      const recurringAdvanceEnd = endOfDay(addDays(start, recurringLimit));
      const recurringLimitEndDateString = formatInTimeZone(recurringAdvanceEnd, 'UTC', 'yyyy-MM-dd');
      const explicitOccurrenceCount =
        recurrence.range.type === 'numbered' ? recurrence.range.numberOfOccurrences : null;
      const occurrenceCount = explicitOccurrenceCount ?? occurrences.length;
      const isNoEnd = recurrence.range.type === 'noEnd';
      const exceedsDaysLimit =
        recurrence.range.type === 'endDate' && recurrence.range.endDate
          ? endOfDay(new Date(`${recurrence.range.endDate}T00:00:00`)) > recurringAdvanceEnd
          : recurrence.range.type === 'numbered'
            ? (() => {
                const lastOccurrence = occurrences.length > 0 ? occurrences[occurrences.length - 1] : null;
                return !!lastOccurrence && lastOccurrence.end > recurringAdvanceEnd;
              })()
            : false;
      const exceedsOccurrencesLimit = occurrenceCount > recurringLimit;
      const isOverLimit = isNoEnd || (isGspace ? exceedsOccurrencesLimit : exceedsDaysLimit);

      if (isOverLimit) {
        const action = this.getRecurringOverLimitActionId();
        if (action === RecurringOverLimitActionId.ScopeOut) {
          effectiveRecurrence = this.buildScopedRecurrence(
            recurrence,
            recurringLimit,
            isGspace,
            recurringLimitEndDateString
          );
          warningReasons.push(
            isGspace
              ? this.translocoService.translate(translation.reservations.recurringEventScopeoutAppliedOccurrences, {
                  occurrences: recurringLimit,
                })
              : this.translocoService.translate(translation.reservations.recurringEventScopeoutAppliedDays, {
                  days: recurringLimit,
                })
          );
        } else if (action === RecurringOverLimitActionId.IgnoreAndWarn) {
          warningReasons.push(
            isGspace
              ? this.translocoService.translate(
                  translation.reservations.recurringEventOverLimitIgnoredOccurrencesWarning,
                  {
                    occurrences: recurringLimit,
                  }
                )
              : this.translocoService.translate(translation.reservations.recurringEventOverLimitIgnoredDaysWarning, {
                  days: recurringLimit,
                })
          );
        } else if (isNoEnd) {
          blockingReasons.push(
            isGspace
              ? this.translocoService.translate(translation.reservations.recurringEventRequiresEndWithinOccurrences, {
                  resourceName: recurringLimitResourceName,
                  occurrences: recurringLimit,
                })
              : this.translocoService.translate(translation.reservations.recurringEventRequiresEndDateWithinDays, {
                  resourceName: recurringLimitResourceName,
                  days: recurringLimit,
                })
          );
        } else {
          blockingReasons.push(
            isGspace
              ? this.translocoService.translate(translation.reservations.recurringEventExceedsMaxOccurrences, {
                  resourceName: recurringLimitResourceName,
                  occurrences: recurringLimit,
                })
              : this.translocoService.translate(translation.reservations.recurringEventExceedsBookingWindow, {
                  resourceName: recurringLimitResourceName,
                  days: recurringLimit,
                })
          );
        }
      }
    }

    const recurrenceForRules = effectiveRecurrence;
    const occurrencesForRules =
      recurrenceForRules === recurrence
        ? occurrences
        : expandRecurrenceOccurrences(recurrenceForRules, startDatetime, endDatetime, start, providerValidationEnd);

    for (const resourceAttendee of this.dialogData.reservation.resourceAttendees) {
      const settings = resourceAttendee.resource.schedulingSettings;
      if (!settings) {
        continue;
      }

      const resourceName =
        fromLocalized(
          resourceAttendee.resource.displayNameForApp ?? resourceAttendee.resource.displayName,
          this.translocoService.getActiveLang(),
          this.translocoService.getDefaultLang()
        ) ?? resourceAttendee.resource.name;

      if (settings.allowRecurringMeetings === false) {
        blockingReasons.push(
          this.translocoService.translate(translation.reservations.recurringMeetingsNotAllowedForResource, {
            resourceName,
          })
        );
        continue;
      }

      const bookingWindowInDays = settings.bookingWindowInDays;
      const enforceSchedulingHorizon = settings.enforceSchedulingHorizon ?? true;
      if (bookingWindowInDays == null || !enforceSchedulingHorizon) {
        continue;
      }

      const horizonEnd = endOfDay(addDays(start, bookingWindowInDays));

      if (recurrenceForRules.range.type === 'noEnd') {
        blockingReasons.push(
          this.translocoService.translate(translation.reservations.recurringEventRequiresEndDateWithinDays, {
            resourceName,
            days: bookingWindowInDays,
          })
        );
        continue;
      }

      if (recurrenceForRules.range.type === 'endDate' && recurrenceForRules.range.endDate) {
        const recurrenceEndDate = endOfDay(new Date(`${recurrenceForRules.range.endDate}T00:00:00`));
        if (recurrenceEndDate > horizonEnd) {
          blockingReasons.push(
            this.translocoService.translate(translation.reservations.recurringEventExceedsBookingWindow, {
              resourceName,
              days: bookingWindowInDays,
            })
          );
        }
        continue;
      }

      if (recurrenceForRules.range.type === 'numbered' && recurrenceForRules.range.numberOfOccurrences != null) {
        const lastOccurrence =
          occurrencesForRules.length > 0 ? occurrencesForRules[occurrencesForRules.length - 1] : null;
        if (lastOccurrence && lastOccurrence.end > horizonEnd) {
          blockingReasons.push(
            this.translocoService.translate(translation.reservations.recurringEventExceedsBookingWindow, {
              resourceName,
              days: bookingWindowInDays,
            })
          );
        }
      }
    }

    return {
      effectiveRecurrence,
      blockingReasons,
      warningReasons,
    };
  }

  private toDisableReason(message: string, level: DisableReasonLevel): DisableReason {
    return createAvailabilityDisableReason(message, level);
  }

  private isRecurringConflictAllowedBySchedulingRules(
    conflictedOccurrences: number,
    totalOccurrences: number
  ): boolean {
    if (conflictedOccurrences === 0) {
      return true;
    }

    const conflictPercentage = totalOccurrences === 0 ? 0 : (conflictedOccurrences / totalOccurrences) * 100;

    for (const resourceAttendee of this.dialogData.reservation.resourceAttendees) {
      const settings = resourceAttendee.resource.schedulingSettings;
      if (!settings) {
        return false;
      }

      const areConflictsAllowed = settings.allowConflicts ?? settings.allowRecurringConflicts;
      if (areConflictsAllowed !== true) {
        return false;
      }

      if (settings.maximumConflictInstances != null && conflictedOccurrences > settings.maximumConflictInstances) {
        return false;
      }

      if (settings.conflictPercentageAllowed != null && conflictPercentage > settings.conflictPercentageAllowed) {
        return false;
      }
    }

    return true;
  }

  patchEvent() {
    // todo: marian vice resourceu
    const patchedSubject = this.getPatchedSubject(
      this.dialogData.reservation.resourceAttendees[0].resource,
      this.start(),
      this.end(),
      [...this.optionalAttendeesDropdownData(), ...this.requiredAttendeesDropdownData()]
    );

    // todo: marian vice resourcu
    const patchedBodyHtml = this.getPatchedBody(
      this.dialogData.reservation.resourceAttendees.map((x) => x.resource),
      this.dialogData.reservation.resourceAttendees[0].resource,

      this.start(),
      this.end(),
      [...this.optionalAttendeesDropdownData(), ...this.requiredAttendeesDropdownData()],
      false
    );
    this.uiState.set('loading');

    const selectedEditScope = this.selectedEditScope();
    const recurrence =
      this.canEditRecurrencePattern() && selectedEditScope === 'seriesMaster' ? this.resolveRecurrenceForSave() : null;
    const effectiveRecurrence = this.resolveEffectiveRecurrenceForSave(recurrence);
    const targetEventId = this.resolvePatchTargetEventId();
    const mutationScope = this.resolveMutationScopeForSingleAwareOperation(selectedEditScope);

    this.calendarService
      .patchEvent(
        this.editForm.value.visibility.value,
        this.editForm.value.showAs,
        targetEventId,
        patchedSubject,
        patchedBodyHtml,
        CalendarEventDatetime.fromBrowserDatetime(this.start()),
        CalendarEventDatetime.fromBrowserDatetime(this.end()),
        this.dialogData.reservation.resourceAttendees.map((ra) => ({
          roomName: ra.resource.name,
          roomEmail: ra.resource.email,
        })),
        [
          ...this.dialogData.reservation.resourceAttendees.map((ra) => ({
            isResource: true,
            type: 'required',
            name: ra.resource.name,
            address: ra.resource.email,
          })),
          ...this.optionalAttendeesDropdownData().map((a) => ({
            isResource: false,
            address: a.value,
            name: a.text,
            type: 'optional' as any,
          })),
          ...this.requiredAttendeesDropdownData().map((a) => ({
            isResource: false,
            address: a.value,
            name: a.text,
            type: 'required' as any,
          })),
        ],
        this.configService.isO365()
          ? this.editForm.value.teams
            ? {
                onlineMeetingProvider: 'teamsForBusiness',
              }
            : null
          : this.editForm.value.meet
            ? {
                onlineMeetingProvider: 'hangoutsMeet',
              }
            : null,
        {
          minutesBeforeStart: this.editForm.value.reminder.minutes,
          enabled: this.editForm.value.reminder.minutes != null,
        },
        effectiveRecurrence,
        selectedEditScope,
        mutationScope,
        {
          isAllDay: this.isAllDay(),
          preserveDateTimes:
            this.configService.isO365() &&
            this.dialogData.reservation.isAllDay &&
            this.isAllDay() &&
            targetEventId === this.dialogData.reservation.id &&
            this.start().getTime() === this.dialogData.reservation.start.utcDateTime.getTime() &&
            this.end().getTime() === this.dialogData.reservation.end.utcDateTime.getTime(),
        }
      )
      .pipe(
        betterLatestFrom(() => this.store.select(selectUser)),
        switchMap(([_, user]) => {
          const orders: OrderDescriptor[] = this.currentOrders().map((cO) => {
            return {
              note: cO.note,
              resourceIdForFiltering: cO.resourceIdForFiltering,
              items: cO.items.map(
                (oi) =>
                  ({
                    note: oi.note,
                    serviceId: oi.serviceId,
                    cancelled: false,
                  }) as OrderItemDescriptor
              ),
            };
          });

          const eventsModifiedTasks = this.dialogData.reservation.resourceAttendees.map((rA) => {
            const foundOrder = orders.find((x) => x.resourceIdForFiltering === rA.resource.idForFiltering);
            return this.apiService.call(
              eventModified({
                data: {
                  id: targetEventId,
                  resourceUsername: rA.resource.username,
                  subject: patchedSubject,
                  showAs: this.dialogData.reservation.showAs.value,
                  realStart: this.start(),
                  realEnd: this.end(),
                  originalRealStart: this.dialogData.reservation.start.utcDateTime,
                  originalRealEnd: this.dialogData.reservation.end.utcDateTime,
                  iCalUId: this.dialogData.reservation.iCalUId,
                  action: 'modify',
                  doneBy: 'assist',
                  organiserEmail: this.authService.getEmail(),
                  organiserName: user.data.givenName + ' ' + user.data.surname,
                  resourceDisplayName: rA.resource.name,
                },
                orders: foundOrder?.items.length > 0 ? [foundOrder] : [],
              })
            );
          });

          return forkJoin(eventsModifiedTasks);
        }),
        delay(this.configService.getMode() === 'touchone-calendar' ? 0 : 3000),
        tap(() => {
          this.store.dispatch(refreshDashboard());
          this.store.dispatch(loadFavourites());
          this.store.dispatch(
            loadEventsPatched({
              startFrom: set(new Date(), {
                hours: 0,
                minutes: 0,
              }),
              endFrom: add(
                set(new Date(), {
                  hours: 0,
                  minutes: 0,
                }),
                { days: 30 }
              ),
            })
          );

          this.store.dispatch(
            eventPatched({
              id: targetEventId,
              start: new Date(this.dialogData.reservation.start.utcDateTime),
              end: new Date(this.dialogData.reservation.end.utcDateTime),
            })
          );
          this.modal.close();
        })
      )
      .subscribe();
  }

  createEvent() {
    // todo: marian vice resourcu
    const patchedSubject = this.getPatchedSubject(
      this.dialogData.reservation.resourceAttendees[0].resource,
      this.start(),
      this.end(),
      [...this.optionalAttendeesDropdownData(), ...this.requiredAttendeesDropdownData()]
    );
    let patchedBodyHtml = this.getPatchedBody(
      this.dialogData.reservation.resourceAttendees.map((x) => x.resource),
      this.dialogData.reservation.resourceAttendees[0].resource,
      this.start(),
      this.end(),
      [...this.optionalAttendeesDropdownData(), ...this.requiredAttendeesDropdownData()],
      true
    );

    const orders: OrderDescriptor[] = this.currentOrders().map((cO) => {
      return {
        note: cO.note,
        resourceIdForFiltering: cO.resourceIdForFiltering,
        items: cO.items.map(
          (oi) =>
            ({
              note: oi.note,
              serviceId: oi.serviceId,
              cancelled: false,
            }) as OrderItemDescriptor
        ),
      };
    });

    const totalOrderItemsCount = this.currentOrders().reduce((acc, it) => acc + it.items.length, 0);
    const recurrence = this.repetitiveEventsEnabled() ? this.resolveRecurrenceForSave() : null;
    const effectiveRecurrence = this.resolveEffectiveRecurrenceForSave(recurrence);
    this.store.dispatch(
      createEvent({
        visibility: this.editForm.value.visibility.value,
        orders: totalOrderItemsCount > 0 ? orders : [],
        webexMeeting:
          this.editForm.value.webex && this.configService.value.webexEnabled && this.webexPaired().data!.paired
            ? {
                ...this.webexPaired()!.data,
              }
            : undefined,
        showAs: this.editForm.value.showAs,
        onlineMeeting: this.configService.isO365()
          ? this.editForm.value.teams
            ? {
                onlineMeetingProvider: 'teamsForBusiness',
              }
            : null
          : this.editForm.value.meet
            ? {
                onlineMeetingProvider: 'hangoutsMeet',
              }
            : null,
        subject: patchedSubject,
        text: patchedBodyHtml,
        start: this.start(),
        end: this.end(),
        isAllDay: this.isAllDay(),
        resourceAttendees: this.dialogData.reservation.resourceAttendees,
        attendees: [
          {
            isResource: true,
            address: this.dialogData.reservation.resourceAttendees[0].resource.email,
            name: this.dialogData.reservation.resourceAttendees[0].resource.name,
            type: 'required',
          },
          ...this.optionalAttendeesDropdownData().map((ax) => ({
            isResource: false,
            address: ax.value,
            name: ax.text,
            type: 'optional' as any,
          })),
          ...this.requiredAttendeesDropdownData().map((ax) => ({
            isResource: false,
            address: ax.value,
            name: ax.text,
            type: 'required' as any,
          })),
        ],
        reminder: {
          minutesBeforeStart: this.editForm.value.reminder.minutes,
          enabled: this.editForm.value.reminder.minutes != null,
        },
        recurrence: effectiveRecurrence,
        skipReservationNavigation: this.dialogData.closeOnSave === true,
        suppressCreatedNotification: this.dialogData.creationWorkflowNotifications === true,
      })
    );

    if (this.dialogData.closeOnSave) {
      this.modal?.close({
        action: 'saved',
        mode: 'new',
        createdEvent: this.createCreatedEventResult(patchedSubject, this.start(), this.end()),
      });
    }
  }

  private createCreatedEventResult(subject: string, start: Date, end: Date): NewReservationCreatedEventResult {
    return {
      subject,
      start,
      end,
      roomEmails: this.dialogData.reservation.resourceAttendees.map(
        (resourceAttendee) => resourceAttendee.resource.email
      ),
    };
  }

  save() {
    if (!this.dateTimeValid()) {
      return;
    }
    if (this.ordersValidity() !== 'VALID') {
      if (this.activeTab() != 2) {
        this.activeTab.set(2);
      }
      return;
    }

    if (this.mode() === 'detail') {
      this.patchEvent();
    }

    if (this.mode() === 'new') {
      this.createEvent();
    }
  }

  getAvailability = (
    availabilityView: CalendarAvailability[],
    target: Date,
    minimum: Date,
    uiState: 'view' | 'edit' | 'loading',
    reservation: ReservationWithHint
  ) => {
    if (uiState === 'edit') {
      return getAvailabilityFromView(availabilityView, minimum, target);
    }

    const startEventIndex = getIndexFromDate(minimum, reservation.start.utcDateTime);
    const endEventIndex = getIndexFromDate(minimum, reservation.end.utcDateTime);
    const current = getIndexFromDate(minimum, target);

    if (current >= startEventIndex && current < endEventIndex) {
      return CalendarAvailability.currentEvent;
    }

    return getAvailabilityFromView(availabilityView, minimum, target);
  };

  onChanged($event: { oldValue: Date; newValue: Date }, isStart: boolean) {
    if (isStart) {
      if ($event.newValue >= this.end()) {
        if (this.endPicker) {
          const updatedDate = this.endPicker().resetValue($event.newValue);

          this.setStartEnd({
            start: $event.newValue,
            end: addMinutes($event.newValue, differenceInMinutes(this.start(), this.end())),
          });
        }
      } else {
        this.setStartEnd({
          start: $event.newValue,
          end: this.end(),
        });
      }
    } else {
      this.setStartEnd({
        end: $event.newValue,
        start: this.start(),
      });
    }
  }

  cancelParticipation() {
    this.store.dispatch(
      cancelParticipationEventById({
        eventToCancelParticipationId: this.dialogData.reservation.id,
        icalId: this.dialogData.reservation.iCalUId,
      })
    );
    this.modal?.close();
  }

  private dispatchDelete(targetEventId: string, mutationScope: CalendarRecurringMutationScope) {
    this.store.dispatch(
      deleteEventById({
        eventToDeleteId: targetEventId,
        icalId: this.dialogData.reservation.iCalUId,
        resourceAttendees: this.dialogData.reservation.resourceAttendees,
        subject: this.dialogData.reservation.subject,
        end: this.dialogData.reservation.end.utcDateTime,
        start: this.dialogData.reservation.start.utcDateTime,
        showAs: this.dialogData.reservation.showAs.value,
        mutationScope,
      })
    );
  }

  onDelete() {
    this.confirmDelete(this.dialogData.reservation.id, 'event', { scope: 'all' });
  }

  onDeleteOccurrence() {
    const mutation = resolveSingleOccurrenceMutation(this.dialogData.reservation, (event) =>
      this.calendarService.getSingleOccurrenceMutationTargetEventId(event)
    );

    if (!mutation) {
      return;
    }

    this.confirmDelete(mutation.targetEventId, 'occurrence', mutation.mutationScope);
  }

  onDeleteSeries() {
    this.confirmDelete(this.dialogData.reservation.seriesMasterId ?? this.dialogData.reservation.id, 'series', {
      scope: 'all',
    });
  }

  eventTimesChanged({ newStart, newEnd }: CalendarEventTimesChangedEvent): void {
    this.setStartEnd({
      start: newStart,
      end: newEnd,
    });
  }

  private getReservationPlaceholderValues(
    resource: AssistResourceBM,
    startDateTime: Date,
    toDateTime: Date,
    attendees: AttendeeDropdownItem[],
    resourceUrl: string
  ): ReservationPlaceholderValues {
    const resourceType = this.configService.getResourceTypeFromId(resource.resourceTypeId);

    return {
      'resource-url': resourceUrl,
      'start-date': this.datePipe.transform(startDateTime, this.pipesService.dateFormat),
      'end-date': this.datePipe.transform(toDateTime, this.pipesService.dateFormat),
      'start-time': this.datePipe.transform(startDateTime, this.pipesService.timeFormat),
      'end-time': this.datePipe.transform(toDateTime, this.pipesService.timeFormat),
      'resource-capacity':
        resource.capacity !== null
          ? resource.capacity.toString()
          : this.translocoService.translate(translation.common.unlimited),
      'attendees-count': attendees.length,
      'resource-display-name':
        fromLocalized(
          resource.displayNameForApp,
          this.translocoService.getActiveLang(),
          this.translocoService.getDefaultLang()
        ) ||
        fromLocalized(
          resource.displayName,
          this.translocoService.getActiveLang(),
          this.translocoService.getDefaultLang()
        ) ||
        resource.name,
      'resource-type':
        (resourceType ? localized(resourceType.name, this.translocoService) : '') || resource.resourceTypeName,
    };
  }

  getPatchedBody(
    allResources: AssistResourceBM[],
    resource: AssistResourceBM,
    startDateTime: Date,
    toDateTime: Date,
    attendees: AttendeeDropdownItem[],
    appendNavigationLinks: boolean
  ) {
    let patchedBodyHtml = replaceReservationPlaceholders(
      this.editForm.value.bodyHtml,
      this.getReservationPlaceholderValues(
        resource,
        startDateTime,
        toDateTime,
        attendees,
        '<a   target="_blank" href="' +
          location.protocol +
          '//' +
          location.host +
          '/dashboard?multiviewResource=' +
          encodeURIComponent(resource.id) +
          '">' +
          fromLocalized(
            resource.displayName,
            this.translocoService.getActiveLang(),
            this.translocoService.getDefaultLang()
          ) +
          '</a>'
      )
    );

    const navigatableResource = allResources.find(
      (x) =>
        x.navigationEnabled &&
        x.mapInfos.some(
          (xxx) => xxx.areaId != null && !this.areas().find((a) => a.id === xxx.areaId)?.notIncludedInNavigation
        )
    );
    if (appendNavigationLinks && navigatableResource) {
      let assistBaseText = this.configService.value.assistNavigationInfo;
      assistBaseText = assistBaseText
        .replaceAll(
          '{assist-app-navigation-url}',
          location.protocol +
            '//' +
            location.host +
            '/navigation?targetResourceId=' +
            navigatableResource.idForFiltering +
            '&targetSelectionMode=8'
        )
        .replaceAll(
          '{navigation-app-url}',
          this.configService.value.navigationAppUrl +
            '?targetResourceId=' +
            navigatableResource.idForFiltering +
            +'&targetSelectionMode=8'
        )
        .replaceAll(
          '{resource-type}',
          localized(
            this.configService.getResourceTypeFromId(navigatableResource.resourceTypeId).name,
            this.translocoService
          )
        )
        .replaceAll(
          '{resource-display-name}',
          fromLocalized(
            resource.displayNameForApp,
            this.translocoService.getActiveLang(),
            this.translocoService.getDefaultLang()
          ) ?? resource.name
        );

      let navigationBaseText = this.configService.value.navigationInfo;
      navigationBaseText = navigationBaseText
        .replaceAll(
          '{assist-app-navigation-url}',
          location.protocol +
            '//' +
            location.host +
            '/navigation?targetResourceId=' +
            navigatableResource.idForFiltering +
            '&targetSelectionMode=8'
        )
        .replaceAll(
          '{navigation-app-url}',
          this.configService.value.navigationAppUrl +
            '?targetResourceId=' +
            navigatableResource.idForFiltering +
            '&targetSelectionMode=8'
        )
        .replaceAll(
          '{resource-type}',
          localized(
            this.configService.getResourceTypeFromId(navigatableResource.resourceTypeId).name,
            this.translocoService
          )
        )
        .replaceAll(
          '{resource-display-name}',
          fromLocalized(
            navigatableResource.displayNameForApp,
            this.translocoService.getActiveLang(),
            this.translocoService.getDefaultLang()
          ) ?? resource.name
        );

      /// je potreba pripadne pridat link na navigacei
      const selectedValue = this.editForm.value.navigationLinkHint!;
      let linkHtml = '';
      if (selectedValue.key == NavigationLinkHintType.NONE) {
        // NONE
        linkHtml = '';
      } else if (selectedValue.key == NavigationLinkHintType.ASSIST) {
        // Assist
        linkHtml = linkHtml + assistBaseText;
      } else if (selectedValue.key == NavigationLinkHintType.NAVIGATION) {
        // Navigation app
        linkHtml = linkHtml + navigationBaseText;
      } else {
        // Assist
        linkHtml = linkHtml + assistBaseText + '<br/>' + navigationBaseText;
      }

      if (linkHtml.length > 0) {
        patchedBodyHtml = patchedBodyHtml + '<hr/><br/>' + linkHtml;
      }
    }
    return patchedBodyHtml;
  }

  getPatchedSubject(
    resource: AssistResourceBM,
    startDateTime: Date,
    toDateTime: Date,
    attendees: AttendeeDropdownItem[]
  ) {
    return replaceReservationPlaceholders(
      this.editForm.value.subject,
      this.getReservationPlaceholderValues(
        resource,
        startDateTime,
        toDateTime,
        attendees,
        location.protocol + '//' + location.host + '/dashboard?multiviewResource=' + encodeURIComponent(resource.id)
      )
    );
  }

  private confirmDelete(
    targetEventId: string,
    scope: 'event' | 'occurrence' | 'series',
    mutationScope: CalendarRecurringMutationScope
  ) {
    this.dialog
      .open(ReservationDeleteComponent, {
        data: {
          reservation: this.dialogData.reservation,
          scope,
        },
      })
      .closed.subscribe((x) => {
        if (x === 1) {
          if (this.modal) {
            this.modal.close(0);
          } else {
            this.close.emit();
          }

          this.dispatchDelete(targetEventId, mutationScope);
        }
      });
  }

  setOrdersValue($event: { resourceIdForFiltering: number; items: any[]; note: string }[]) {
    this.currentOrders.set($event);
  }

  setOrdersValidity($event: FormControlStatus) {
    this.ordersValidity.set($event);
  }

  addMeToAttendees() {
    const user = this.currentUserPack().data;
    const initials = user.name
      ? user.name.trim().split(' ').length > 1
        ? user.name.trim().split(' ')[0][0] + user.name.trim().split(' ')[1][0]
        : ''
      : '';

    this.requiredAttendeesDropdownData.set([
      ...this.requiredAttendeesDropdownData(),
      {
        color: 'red',
        value: user.email,
        text: user.name ?? user.email,
        initials: initials,
        isGroup: false,
      },
    ]);
  }

  onFinish() {
    this.dialog
      .open<number, FinishReservationData>(ReservationFinishComponent, {
        data: {
          reservation: this.dialogData.reservation,
        },
      })
      .closed.subscribe((x) => {
        if (x === 1) {
          this.store.dispatch(
            finishEventById({
              eventToFinishId: this.dialogData.reservation.id,
              finishedStatusRequests: getEventFinishRequests(this.dialogData.reservation),
            })
          );
          this.modal?.close(0);
        }
      });
  }

  qrService = inject(QRService);

  onConfirm() {
    this.qrService.trigger$.next(null);
  }

  getExpanderTexts(resourceAttendee: ResourceAttendee, resourceTitle: string) {
    const startDayOfNow = startOfDay(this.now);
    const datas = resourceAttendee.statusesByDay.filter((day) => {
      const startOfDayDay = startOfDay(new Date(day.date));
      return startOfDayDay <= startDayOfNow;
    });

    return datas.map((data) => {
      if (data.confirmed) {
        const text = this.translocoService.translate(translation.reservations.confirmed, {
          when: this.dateTimeFormatPipe.transform(resourceAttendee.confirmedWhen),
          who: resourceAttendee.confirmedBy,
        });
        return this.dateFormatPipe.transform(new Date(data.date)) + ': ' + text;
      } else {
        const text = this.translocoService.translate(translation.reservations.unconfirmed);
        return this.dateFormatPipe.transform(new Date(data.date)) + ': ' + text;
      }
    });
  }

  getExpandeTitle(resourceAttendee: ResourceAttendee, resourceTitle: string) {
    const afterEnd = this.dialogData.reservation.afterEnd;
    const beforeStart = this.dialogData.reservation.start.utcDateTime > this.now;
    const allDaysConfirmed = resourceAttendee.statusesByDay.every((x) => x.confirmed);
    const neverConfirmed = resourceAttendee.statusesByDay.every((x) => !x.confirmed);
    if (afterEnd) {
      if (allDaysConfirmed) {
        return this.translocoService.translate(translation.reservations.confirmedEveryDay);
      }

      if (neverConfirmed) {
        return this.translocoService.translate(translation.reservations.confirmedNever);
      }
      return this.translocoService.translate(translation.reservations.confirmedSomeDay);
    }

    if (beforeStart) {
      return '';
    }

    if (resourceAttendee.confirmAvailable) {
      return this.translocoService.translate(translation.reservations.confirmationRequiredUntil, {
        until: this.confirmTo(resourceAttendee),
        resourceName: resourceTitle,
      });
    } else if (resourceAttendee.status === 'need-confirmation') {
      return this.translocoService.translate(translation.reservations.confirmationRequiredBetween, {
        from: this.confirmFrom(resourceAttendee),
        to: this.confirmTo(resourceAttendee),
        resourceName: resourceTitle,
      });
    }
    // "Tak nic"
    return resourceTitle;
  }

  anyPremiumResourceLikeInAttendees = computed(() => {
    return this.dialogData.reservation.resourceAttendees.some((x) =>
      this.licensingService.isPremiumResourceLike(x.resource)
    );
  });
  protected readonly getTextKeyAndClass = getTextKeyAndClass;
}
